import React from 'react';
import { NumericFormat } from 'react-number-format';
import { Typography } from '@mui/material';
import { compact } from 'lodash';
import { useStyles as useTableStyles } from 'components/Table';
import { Api, FeeAccounting, FeeType } from 'further-types/investment';

type Fee = Api.GetInvestmentFeesResponse['fees'][number];

type Props = {
  fees: Array<Fee>;
  columnCount: number;
};

export const FeeTotals: React.FC<Props> = ({ fees, columnCount }) => {
  const { classes: tableClasses } = useTableStyles();

  const totals = {
    total: {
      netFee: 0,
      grossFee: 0,
    },
    cash: { netFee: 0, grossFee: 0 },
    accrued: { netFee: 0, grossFee: 0 },
  };

  compact(fees).forEach((rec) => {
    const isDiscount = rec?.feeType === FeeType.Discount;
    const { feeExVAT = 0, feeIncVAT = 0 } = rec;

    if (isDiscount) {
      totals.total.netFee -= feeExVAT;
      totals.total.grossFee -= feeIncVAT;
    } else {
      totals.total.netFee += feeExVAT;
      totals.total.grossFee += feeIncVAT;
    }

    if (rec?.feeAccounting === FeeAccounting.ChargedToInvestor) {
      if (isDiscount) {
        totals.cash.netFee -= feeExVAT;
        totals.cash.grossFee -= feeIncVAT;
      } else {
        totals.cash.netFee += feeExVAT;
        totals.cash.grossFee += feeIncVAT;
      }
    } else if (rec?.feeAccounting === FeeAccounting.Accrued) {
      if (isDiscount) {
        totals.accrued.netFee -= feeExVAT;
        totals.accrued.grossFee -= feeIncVAT;
      } else {
        totals.accrued.netFee += feeExVAT;
        totals.accrued.grossFee += feeIncVAT;
      }
    }
  });

  return (
    <>
      <tr className={tableClasses.tFootRow}>
        <td className={tableClasses.tFootCell} colSpan={4}>
          <Typography component="h4" variant="inherit">
            Total
          </Typography>
        </td>
        <td className={tableClasses.tFootCell}>
          <NumericFormat
            decimalScale={2}
            fixedDecimalScale
            value={totals.total?.netFee}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'£'}
          />
        </td>
        <td className={tableClasses.tFootCell} colSpan={columnCount - 5}>
          <NumericFormat
            decimalScale={2}
            fixedDecimalScale
            value={totals.total?.grossFee}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'£'}
          />
        </td>
      </tr>
      <tr className={tableClasses.tFootRow}>
        <td className={tableClasses.tFootCell} colSpan={4}>
          <Typography component="h4" variant="inherit">
            Of which cash
          </Typography>
        </td>
        <td className={tableClasses.tFootCell}>
          <NumericFormat
            decimalScale={2}
            fixedDecimalScale
            value={totals.cash.netFee}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'£'}
          />
        </td>
        <td className={tableClasses.tFootCell} colSpan={columnCount - 5}>
          <NumericFormat
            decimalScale={2}
            fixedDecimalScale
            value={totals.cash.grossFee}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'£'}
          />
        </td>
      </tr>
      <tr className={tableClasses.tFootRow}>
        <td className={tableClasses.tFootCell} colSpan={4}>
          <Typography component="h4" variant="inherit">
            Of which accrued
          </Typography>
        </td>
        <td className={tableClasses.tFootCell}>
          <NumericFormat
            decimalScale={2}
            fixedDecimalScale
            value={totals.accrued.netFee}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'£'}
          />
        </td>
        <td className={tableClasses.tFootCell} colSpan={columnCount - 5}>
          <NumericFormat
            decimalScale={2}
            fixedDecimalScale
            value={totals.accrued.grossFee}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'£'}
          />
        </td>
      </tr>
    </>
  );
};
